import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { PackageCheck, Truck, RefreshCw, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Abhollager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkout, setCheckout] = useState({ open: false, item: null, qty: 1 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pickup");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const key = it.last_project || "—";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(it);
    }
    // sort: items with project first, then '—' last
    return [...m.entries()].sort((a, b) =>
      a[0] === "—" ? 1 : b[0] === "—" ? -1 : a[0].localeCompare(b[0])
    );
  }, [items]);

  const totalQty = items.reduce((s, it) => s + (it.quantity_pickup || 0), 0);
  const totalEk = items.reduce((s, it) => s + (it.quantity_pickup || 0) * (it.ek_price || 0), 0);

  const startCheckout = (it) => {
    setCheckout({ open: true, item: it, qty: it.quantity_pickup });
  };

  const doCheckout = async () => {
    const { item, qty } = checkout;
    if (!item) return;
    const n = Math.max(1, Math.min(item.quantity_pickup, Number(qty) || 0));
    try {
      await api.post(`/articles/${item.article_id}/adjust`, {
        delta: -n,
        type: "entnahme",
        location: "pickup",
        project: item.last_project || "Abholung",
        note: "Aus Abhollager mitgenommen",
      });
      toast.success(`Ausgecheckt: ${n}× ${item.name}`);
      setCheckout({ open: false, item: null, qty: 1 });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const checkoutWholeProject = async (project, projectItems) => {
    if (!window.confirm(`Alle ${projectItems.length} Positionen für „${project}" auschecken?`)) return;
    try {
      const { data } = await api.post(`/pickup/bulk-checkout`, {
        items: projectItems.map((it) => ({ article_id: it.article_id, qty: it.quantity_pickup })),
        project: project === "—" ? "Abholung" : project,
        note: "Sammelabholung aus Abhollager",
      });
      toast.success(`Projekt „${project}" — ${data.checked_out} Positionen ausgecheckt.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fmt = (n) =>
    new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="pickup-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
            ABHOLLAGER · {items.length} POSITIONEN · {totalQty} STK.
          </div>
          <h1 className="font-display text-4xl uppercase flex items-center gap-3">
            <PackageCheck className="w-8 h-8" /> Abhollager
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Was muss heute mitgenommen werden? Pro Projekt gruppiert. Mit „Auschecken" buchst du
            Material aus dem Abhollager raus — landet automatisch in der Historie mit deinem Namen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={load}
            variant="outline"
            data-testid="pickup-reload"
            className="rounded-sm border-zinc-400 h-11"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div
          className="bg-white border border-zinc-200 rounded-sm p-4"
          style={{ borderLeft: "4px solid #9333EA" }}
        >
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">PROJEKTE</div>
          <div className="font-display text-3xl font-mono-num mt-1">{grouped.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4" style={{ borderLeft: "4px solid #F97316" }}>
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">STÜCK GESAMT</div>
          <div className="font-display text-3xl font-mono-num mt-1">{totalQty}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4" style={{ borderLeft: "4px solid #16A34A" }}>
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">EK-WERT</div>
          <div className="font-display text-3xl font-mono-num mt-1">€ {fmt(totalEk)}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm mono text-zinc-500">LADEN…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
          <Truck className="w-12 h-12 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-display text-xl uppercase mb-1">Abhollager leer</div>
          <p className="text-sm text-zinc-600 max-w-md mx-auto">
            Es liegt nichts im Abhollager. Lege Material per <Link to="/scanner" className="text-[#F97316] underline font-medium">Scanner → Umlagern</Link> ins
            Abhollager, damit es hier morgens erscheint.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([project, projectItems]) => {
            const pTotal = projectItems.reduce((s, it) => s + (it.quantity_pickup || 0), 0);
            const pEk = projectItems.reduce((s, it) => s + (it.quantity_pickup || 0) * (it.ek_price || 0), 0);
            return (
              <div
                key={project}
                data-testid={`project-group-${project}`}
                className="bg-white border border-zinc-200 rounded-sm overflow-hidden"
              >
                <div className="px-5 py-3 bg-zinc-900 text-white flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="mono text-[10px] tracking-[0.25em] text-zinc-400">PROJEKT</div>
                    <div className="font-display text-lg uppercase">
                      {project === "—" ? "Ohne Projekt" : project}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-xs mono text-zinc-300">
                      {projectItems.length} POS · {pTotal} STK · €&nbsp;{fmt(pEk)}
                    </div>
                    <Button
                      onClick={() => checkoutWholeProject(project, projectItems)}
                      data-testid={`checkout-all-${project}`}
                      className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-9"
                    >
                      Alles auschecken
                    </Button>
                  </div>
                </div>

                {/* Desktop table */}
                <div className="hidden sm:block">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-100 text-zinc-600">
                      <tr>
                        <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">ARTIKEL</th>
                        <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">PLATZ</th>
                        <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">EINGELAGERT VON</th>
                        <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">MENGE</th>
                        <th className="text-right px-4 py-2 mono text-[10px] tracking-wider w-44">AKTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectItems.map((it) => (
                        <tr key={it.article_id} className="border-t border-zinc-100" data-testid={`pickup-row-${it.article_id}`}>
                          <td className="px-4 py-3">
                            <Link to={`/artikel/${it.article_id}`} className="font-medium hover:text-[#F97316]">
                              {it.name}
                            </Link>
                            <div className="mono text-[11px] text-zinc-500">{it.ean}</div>
                          </td>
                          <td className="px-4 py-3 mono text-xs">
                            {[it.shelf_label, it.compartment, it.slot].filter(Boolean).join(" · ") || "—"}
                            {it.location_text && (
                              <div className="text-[11px] text-zinc-500 normal-case mt-0.5">{it.location_text}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <div>{it.last_assigned_by || "—"}</div>
                            <div className="mono text-[11px] text-zinc-500">
                              {it.last_assigned_at ? new Date(it.last_assigned_at).toLocaleString("de-DE") : ""}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-display text-2xl font-mono-num">
                            {it.quantity_pickup}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              onClick={() => startCheckout(it)}
                              data-testid={`checkout-${it.article_id}`}
                              className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-9"
                            >
                              <PackageCheck className="w-4 h-4 mr-1" /> Auschecken
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="sm:hidden divide-y divide-zinc-100">
                  {projectItems.map((it) => (
                    <div key={it.article_id} className="p-4" data-testid={`pickup-card-${it.article_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <Link to={`/artikel/${it.article_id}`} className="font-medium hover:text-[#F97316]">
                            {it.name}
                          </Link>
                          <div className="mono text-[11px] text-zinc-500">{it.ean}</div>
                          <div className="mt-1 flex items-center gap-1 text-xs text-zinc-600">
                            <MapPin className="w-3 h-3" />
                            {[it.shelf_label, it.compartment, it.slot].filter(Boolean).join(" · ") || "—"}
                          </div>
                          {it.last_assigned_by && (
                            <div className="mt-1 text-[11px] text-zinc-500">
                              Eingelagert: {it.last_assigned_by}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-display text-2xl font-mono-num">{it.quantity_pickup}</div>
                          <div className="mono text-[9px] text-zinc-500">STK.</div>
                        </div>
                      </div>
                      <Button
                        onClick={() => startCheckout(it)}
                        data-testid={`checkout-mobile-${it.article_id}`}
                        className="w-full mt-3 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11"
                      >
                        <PackageCheck className="w-4 h-4 mr-2" /> Auschecken
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Single checkout dialog */}
      <AlertDialog
        open={checkout.open}
        onOpenChange={(o) => !o && setCheckout({ open: false, item: null, qty: 1 })}
      >
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Auschecken</AlertDialogTitle>
            <AlertDialogDescription>
              {checkout.item ? (
                <>
                  <span className="font-medium text-zinc-900">{checkout.item.name}</span><br />
                  Verfügbar im Abhollager: {checkout.item.quantity_pickup} Stk.<br />
                  Projekt: <em>{checkout.item.last_project || "Ohne Projekt"}</em>
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Menge</Label>
            <Input
              type="number"
              min={1}
              max={checkout.item?.quantity_pickup || 1}
              value={checkout.qty}
              onChange={(e) => setCheckout((s) => ({ ...s, qty: Number(e.target.value) }))}
              data-testid="checkout-qty-input"
              className="mt-1.5 rounded-sm border-zinc-400 font-mono-num text-xl h-12 text-center"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={doCheckout}
              data-testid="checkout-confirm"
              className="bg-[#F97316] hover:bg-[#EA580C] rounded-sm"
            >
              <PackageCheck className="w-4 h-4 mr-2" /> Auschecken
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
